import { lazy, Suspense } from "react";
import { Outlet } from "react-router-dom";
// auth
import { AuthGuard } from "src/auth/guard";
// layouts
import DashboardLayout from "src/layouts/dashboard";
// components
import { LoadingScreen } from "src/components/loading-screen";
// api
import { endpoint as endpointLocation } from "src/api/location";
import { endpoint as endpointEquipment } from "src/api/equipment";
import { endpoint as endpointAsset } from "src/api/asset";
import { RefsProvider } from "src/consuman-components/generic/context/refsContext";
import RefsComponents from "src/consuman-components/generic/refs-components/refs-components";
// ----------------------------------------------------------------------

// OVERVIEW;
const TestPage = lazy(() => import("src/pages/dashboard/test-components/new"));
const UserRoleGrid = lazy(() => import("src/pages/dashboard/user-role/user-role-grid"));
const UserRoleForm = lazy(() => import("src/pages/dashboard/user-role/user-role-form"));
const OverviewEcommercePage = lazy(() => import("src/pages/dashboard/ecommerce"));
const OverviewAnalyticsPage = lazy(() => import("src/pages/dashboard/analytics"));
const OverviewBankingPage = lazy(() => import("src/pages/dashboard/banking"));
const OverviewBookingPage = lazy(() => import("src/pages/dashboard/booking"));
const OverviewFilePage = lazy(() => import("src/pages/dashboard/file"));
// PRODUCT
const ProductDetailsPage = lazy(() => import("src/pages/dashboard/product/details"));
const ProductListPage = lazy(() => import("src/pages/dashboard/product/list"));
const ProductCreatePage = lazy(() => import("src/pages/dashboard/product/new"));
const ProductEditPage = lazy(() => import("src/pages/dashboard/product/edit"));
// ORDER
const OrderListPage = lazy(() => import("src/pages/dashboard/order/list"));
const OrderDetailsPage = lazy(() => import("src/pages/dashboard/order/details"));
// INVOICE
const InvoiceListPage = lazy(() => import("src/pages/dashboard/invoice/list"));
const InvoiceDetailsPage = lazy(() => import("src/pages/dashboard/invoice/details"));
const InvoiceCreatePage = lazy(() => import("src/pages/dashboard/invoice/new"));
const InvoiceEditPage = lazy(() => import("src/pages/dashboard/invoice/edit"));
// USER
const UserProfile = lazy(() => import("src/pages/dashboard/user/user-profile"));
const UserGrid = lazy(() => import("src/pages/dashboard/user/user-grid"));
const UserForm = lazy(() => import("src/pages/dashboard/user/user-form"));
const UserAttachments = lazy(() => import("src/pages/dashboard/user/user-attachments"));
const UserImport = lazy(() => import("src/pages/dashboard/user/user-import"));
// PROVIDER
const ProviderForm = lazy(() => import("src/pages/dashboard/provider/provider-form"));
const ProviderGrid = lazy(() => import("src/pages/dashboard/provider/provider-grid"));
const ProviderAttachments = lazy(() => import("src/pages/dashboard/provider/provider-attachments"));
const ProviderImport = lazy(() => import("src/pages/dashboard/provider/provider-import"));
// ACTIVITY TO DATE
const ActivityToDate = lazy(() =>
  import("src/pages/dashboard/planned-task/components/activity-to-date/activity-to-date-form")
);
// PLANNED TASK
const PlannedTaskForm = lazy(() => import("src/pages/dashboard/planned-task/planned-task-form"));
const PlannedTaskGrid = lazy(() => import("src/pages/dashboard/planned-task/planned-task-grid"));
// WORK REQUEST
const WorkRequestForm = lazy(() => import("src/pages/dashboard/work-request/work-request-form"));
const WorkRequestGrid = lazy(() => import("src/pages/dashboard/work-request/work-request-grid"));
const WorkRequestAttachments = lazy(() =>
  import("src/pages/dashboard/work-request/work-request-attachments")
);
const WorkRequestDocument = lazy(() =>
  import("src/pages/dashboard/work-request/work-request-document")
);
const WorkRequestConformity = lazy(() =>
  import("src/pages/dashboard/work-request/work-request-conformity")
);
// PROGRAM
const ProgramForm = lazy(() => import("src/pages/dashboard/program/program-form"));
const ProgramGrid = lazy(() => import("src/pages/dashboard/program/program-grid"));
const ProgramImport = lazy(() => import("src/pages/dashboard/program/program-import"));
// WORK ORDER
const WorkOrderGrid = lazy(() => import("src/pages/dashboard/work-order/work-order-task-grid"));
const WorkOrderTaskForm = lazy(() =>
  import("src/pages/dashboard/work-order/work-order-task/work-order-task-form")
);
const WorkOrderDocument = lazy(() => import("src/pages/dashboard/work-order/work-order-document"));
const WorkOrderTaskAttachments = lazy(() =>
  import("src/pages/dashboard/work-order/work-order-task/work-order-task-attachments")
);
const WorkOrderForm = lazy(() => import("src/pages/dashboard/work-order/work-order-form"));
// TASK
const TaskForm = lazy(() => import("src/pages/dashboard/task/task-form"));
const TaskGrid = lazy(() => import("src/pages/dashboard/task/task-grid"));
const TaskAttachments = lazy(() => import("src/pages/dashboard/task/task-attachments"));
const TaskImport = lazy(() => import("src/pages/dashboard/task/task-import"));
// TASKGROUP
const TaskGroupForm = lazy(() => import("src/pages/dashboard/task-group/task-group-form"));
const TaskGroupGrid = lazy(() => import("src/pages/dashboard/task-group/task-group-grid"));
// EQUIPMENTSTOP
const EquipmentStopForm = lazy(() =>
  import("src/pages/dashboard/equipment-stop/equipment-stop-form")
);
const EquipmentStopGrid = lazy(() =>
  import("src/pages/dashboard/equipment-stop/equipment-stop-grid")
);
// SCHEDULEDOPERATION
const ScheduledOperationForm = lazy(() =>
  import("src/pages/dashboard/scheduled-operation/scheduled-operation-form")
);
const ScheduledOperationGrid = lazy(() =>
  import("src/pages/dashboard/scheduled-operation/scheduled-operation-grid")
);
// STRUCTURE
const StructureForm = lazy(() => import("src/pages/dashboard/structure/structure-form"));
const StructureGrid = lazy(() => import("src/pages/dashboard/structure/structure-grid"));
const StructureImport = lazy(() => import("src/pages/dashboard/structure/structure-import"));
// BLOG
const BlogPostsPage = lazy(() => import("src/pages/dashboard/post/list"));
const BlogPostPage = lazy(() => import("src/pages/dashboard/post/details"));
const BlogNewPostPage = lazy(() => import("src/pages/dashboard/post/new"));
const BlogEditPostPage = lazy(() => import("src/pages/dashboard/post/edit"));
// JOB
const JobDetailsPage = lazy(() => import("src/pages/dashboard/job/details"));
const JobListPage = lazy(() => import("src/pages/dashboard/job/list"));
const JobCreatePage = lazy(() => import("src/pages/dashboard/job/new"));
const JobEditPage = lazy(() => import("src/pages/dashboard/job/edit"));
// TOUR
const TourDetailsPage = lazy(() => import("src/pages/dashboard/tour/details"));
const TourListPage = lazy(() => import("src/pages/dashboard/tour/list"));
const TourCreatePage = lazy(() => import("src/pages/dashboard/tour/new"));
const TourEditPage = lazy(() => import("src/pages/dashboard/tour/edit"));
// SYSTEM
const LabelEditPage = lazy(() => import("src/pages/dashboard/system/label/edit"));
const GlobalConfigurationPage = lazy(() =>
  import("src/pages/dashboard/system/global-configuration/global-configuration-form")
);
const McpConnectionsPage = lazy(() => import("src/pages/dashboard/system/mcp-connections/list"));
// FILE MANAGER
const FileManagerPage = lazy(() => import("src/pages/dashboard/file-manager"));
// PARAMETER
const ParameterGrid = lazy(() => import("src/pages/dashboard/parameter/parameter-grid"));
const ParameterForm = lazy(() => import("src/pages/dashboard/parameter/parameter-form"));
const ParameterImport = lazy(() => import("src/pages/dashboard/parameter/parameter-import"));
// PARAMETER TYPE
const ParameterTypeGrid = lazy(() =>
  import("src/pages/dashboard/parameter-type/parameter-type-grid")
);
const ParameterTypeForm = lazy(() =>
  import("src/pages/dashboard/parameter-type/parameter-type-form")
);
// SUPPLY
const SupplyGrid = lazy(() => import("src/pages/dashboard/supply/supply-grid"));
const SupplyForm = lazy(() => import("src/pages/dashboard/supply/supply-form"));
const SupplyAttachments = lazy(() => import("src/pages/dashboard/supply/supply-attachments"));
const SupplyImport = lazy(() => import("src/pages/dashboard/supply/supply-import"));
// ASSET
const AssetGrid = lazy(() => import("src/pages/dashboard/asset/asset-grid"));
const AssetForm = lazy(() => import("src/pages/dashboard/asset/asset-form"));
const AssetImport = lazy(() => import("src/pages/dashboard/asset/asset-import"));
const AssetAttachments = lazy(() => import("src/pages/dashboard/asset/asset-attachments"));
// WORK TEAM
const WorkTeamForm = lazy(() => import("src/pages/dashboard/work-team/work-team-form"));
// COMPONENT
const ComponentGrid = lazy(() => import("src/pages/dashboard/component/component-grid"));
const ComponentForm = lazy(() => import("src/pages/dashboard/component/component-form"));
const ComponentImport = lazy(() => import("src/pages/dashboard/component/component-import"));
// CURRENCY
const CurrencyGrid = lazy(() => import("src/pages/dashboard/currency/currency-grid"));
// CATEGORY
const CategoryGrid = lazy(() => import("src/pages/dashboard/category/category-grid"));
// METER
const MeterGrid = lazy(() => import("src/pages/dashboard/meter/meter-grid"));
const MeterForm = lazy(() => import("src/pages/dashboard/meter/meter-form"));

// READING
const ReadingGrid = lazy(() => import("src/pages/dashboard/reading/reading-grid"));
const ImportReading = lazy(() => import("src/pages/dashboard/reading/reading-import"));
// AUDIT
const AuditGrid = lazy(() => import("src/pages/dashboard/audit/audit-grid"));
// IMPORT ENTITY
const ImportEntityForm = lazy(() => import("src/pages/dashboard/import-entity/import-entity-form"));
// REPORTS DASHBOARD
const ReportsDashboard = lazy(() =>
  import("src/pages/dashboard/reports-dashboard/reports-dashboard")
);
const ReportsGrid = lazy(() => import("src/pages/dashboard/reports-dashboard/reports-grid"));
// EXCHANGE RATE
const ExchangeRateGrid = lazy(() => import("src/pages/dashboard/exchange-rate/exchange-rate-grid"));
// CONFIGURATION
const FieldConfiguration = lazy(() =>
  import("src/pages/dashboard/field-configuration/field-configuration-form")
);
// DASHBOARD
const Dashboard = lazy(() => import("src/pages/dashboard/dashboard/dashboard"));
// APP
const ChatPage = lazy(() => import("src/pages/dashboard/chat"));
const MailPage = lazy(() => import("src/pages/dashboard/mail"));
const CalendarPage = lazy(() => import("src/pages/dashboard/calendar"));
const KanbanPage = lazy(() => import("src/pages/dashboard/kanban"));
// TEST RENDER PAGE BY ROLE
const PermissionDeniedPage = lazy(() => import("src/pages/dashboard/permission"));
// BLANK PAGE
const BlankPage = lazy(() => import("src/pages/dashboard/blank"));
// VOUCHER
const VoucherForm = lazy(() => import("src/pages/dashboard/voucher/voucher-form"));
const VoucherGrid = lazy(() => import("src/pages/dashboard/voucher/voucher-grid"));
const VoucherDocument = lazy(() => import("src/pages/dashboard/voucher/voucher-document"));
const VoucherAttachments = lazy(() => import("src/pages/dashboard/voucher/voucher-attachments"));
// VOUCHER TYPE
const VoucherTypeForm = lazy(() => import("src/pages/dashboard/voucher-type/voucher-type-form"));
const VoucherTypeGrid = lazy(() => import("src/pages/dashboard/voucher-type/voucher-type-grid"));
// WELCOME
const WelcomePage = lazy(() => import("src/pages/dashboard/welcome"));
// ENTITY REDIRECT (deep links generados por la IA, ver paths.dashboard.open)
const EntityRedirectPage = lazy(() => import("src/pages/dashboard/entity-redirect"));

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: "app",
    element: (
      <AuthGuard>
        <RefsProvider>
          <DashboardLayout>
            <RefsComponents />
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </DashboardLayout>
        </RefsProvider>
      </AuthGuard>
    ),
    children: [
      { element: <WelcomePage />, index: true },
      { path: "dashboard", element: <Dashboard /> },
      { path: "testPage", element: <TestPage /> },
      { path: "ecommerce", element: <OverviewEcommercePage /> },
      { path: "analytics", element: <OverviewAnalyticsPage /> },
      { path: "banking", element: <OverviewBankingPage /> },
      { path: "booking", element: <OverviewBookingPage /> },
      { path: "file", element: <OverviewFilePage /> },
      {
        path: "parameter",
        children: [
          { element: <ParameterGrid />, index: true },
          { path: "grid", element: <ParameterGrid /> },
          { path: "form", element: <ParameterForm /> },
          { path: ":id/undelete", element: <ParameterForm undelete /> },
          { path: ":id/form", element: <ParameterForm /> },
          { path: ":id/:copy/form", element: <ParameterForm /> },
          { path: "parameter-type", element: <ParameterTypeGrid />, index: true },
          { path: "parameter-type/grid", element: <ParameterTypeGrid /> },
          { path: "parameter-type/form", element: <ParameterTypeForm /> },
          { path: "parameter-type/:id/form", element: <ParameterTypeForm /> },
          { path: "parameter-type/:id/:copy/form", element: <ParameterTypeForm /> },
          { path: "import", element: <ParameterImport /> },
        ],
      },
      {
        path: "audit",
        children: [
          { element: <AuditGrid />, index: true },
          { path: "grid", element: <AuditGrid /> },
        ],
      },
      {
        path: "currency",
        children: [
          { element: <CurrencyGrid />, index: true },
          { path: "grid", element: <CurrencyGrid /> },
          { path: "exchange-rate", element: <ExchangeRateGrid />, index: true },
          { path: "exchange-rate/grid", element: <ExchangeRateGrid /> },
        ],
      },
      {
        path: "category",
        children: [{ path: "grid", element: <CategoryGrid /> }],
      },
      {
        path: "import-entity",
        children: [{ path: ":id/:endpoint/form", element: <ImportEntityForm /> }],
      },
      {
        path: "field-configuration",
        children: [{ path: ":id/form", element: <FieldConfiguration /> }],
      },
      {
        path: "supply",
        children: [
          { element: <SupplyGrid />, index: true },
          { path: "grid", element: <SupplyGrid /> },
          { path: "form", element: <SupplyForm /> },
          { path: ":id/form", element: <SupplyForm /> },
          { path: ":id/undelete", element: <SupplyForm undelete /> },
          { path: ":id/:copy/form", element: <SupplyForm /> },
          { path: ":id/attachments", element: <SupplyAttachments /> },
          { path: ":id/detail", element: <SupplyForm /> },
          { path: "import", element: <SupplyImport /> },
        ],
      },
      {
        path: "planned-task",
        children: [
          { element: <PlannedTaskGrid />, index: true },
          { path: "grid", element: <PlannedTaskGrid /> },
          { path: "form", element: <PlannedTaskForm /> },
          { path: ":id/form", element: <PlannedTaskForm /> },
        ],
      },
      {
        path: "activity-to-date",
        children: [
          { element: <ActivityToDate />, index: true },
          { path: "form", element: <ActivityToDate /> },
        ],
      },
      {
        path: "provider",
        children: [
          { element: <ProviderGrid />, index: true },
          { path: "form", element: <ProviderForm /> },
          { path: "grid", element: <ProviderGrid /> },
          { path: ":id/form", element: <ProviderForm /> },
          { path: ":id/undelete", element: <ProviderForm undelete /> },
          { path: ":id/:copy/form", element: <ProviderForm /> },
          { path: ":id/attachments", element: <ProviderAttachments /> },
          { path: "import", element: <ProviderImport /> },
        ],
      },
      {
        path: "work-request",
        children: [
          { element: <WorkRequestGrid />, index: true },
          { path: "form", element: <WorkRequestForm /> },
          { path: "grid", element: <WorkRequestGrid /> },
          { path: ":id/form", element: <WorkRequestForm /> },
          { path: ":category/category", element: <WorkRequestForm /> },
          { path: ":id/:copy/form", element: <WorkRequestForm /> },
          { path: ":id/attachments", element: <WorkRequestAttachments /> },
          { path: ":id/document", element: <WorkRequestDocument /> },
          { path: ":id/conformity", element: <WorkRequestConformity /> },
          { path: ":id/conformity/:rating", element: <WorkRequestConformity /> },
        ],
      },
      {
        path: "work-order",
        children: [
          { element: <WorkOrderGrid />, index: true },
          { path: "form", element: <WorkOrderForm /> },
          { path: "grid", element: <WorkOrderGrid /> },
          { path: ":id/form", element: <WorkOrderForm /> },
          { path: ":id/document", element: <WorkOrderDocument /> },
        ],
      },
      {
        path: "reports-dashboard",
        children: [
          { element: <ReportsDashboard />, index: true },
          { path: ":key/grid", element: <ReportsGrid /> },
        ],
      },
      {
        path: "work-order-task",
        children: [
          { element: <WorkOrderTaskForm />, index: true },
          { path: "form", element: <WorkOrderTaskForm /> },
          { path: "grid", element: <WorkOrderGrid /> },
          { path: ":id/form", element: <WorkOrderTaskForm /> },
          { path: ":id/attachments", element: <WorkOrderTaskAttachments /> },
        ],
      },
      {
        path: "program",
        children: [
          { element: <ProgramGrid />, index: true },
          { path: "form", element: <ProgramForm /> },
          { path: "grid", element: <ProgramGrid /> },
          { path: "import", element: <ProgramImport /> },
          { path: ":id/form", element: <ProgramForm /> },
          { path: ":id/:copy/form", element: <ProgramForm /> },
        ],
      },
      {
        path: "task",
        children: [
          { element: <TaskGrid />, index: true },
          { path: "form", element: <TaskForm /> },
          { path: "grid", element: <TaskGrid /> },
          { path: ":id/form", element: <TaskForm /> },
          { path: ":id/:copy/form", element: <TaskForm /> },
          { path: ":id/undelete", element: <TaskForm undelete /> },
          { path: ":id/attachments", element: <TaskAttachments /> },
          { path: "task-group", element: <TaskGroupGrid />, index: true },
          { path: "task-group/form", element: <TaskGroupForm /> },
          { path: "task-group/grid", element: <TaskGroupGrid /> },
          { path: "task-group/:id/form", element: <TaskGroupForm /> },
          { path: "task-group/:id/undelete", element: <TaskGroupForm undelete /> },
          { path: "task-group/:id/:copy/form", element: <TaskGroupForm /> },
          { path: "import", element: <TaskImport /> },
        ],
      },
      {
        path: "equipment-stop",
        children: [
          { element: <EquipmentStopGrid />, index: true },
          { path: "form", element: <EquipmentStopForm /> },
          { path: "grid", element: <EquipmentStopGrid /> },
          { path: ":id/form", element: <EquipmentStopForm /> },
          { path: ":id/:copy/form", element: <EquipmentStopForm /> },
          { path: "scheduled-operation", element: <ScheduledOperationGrid />, index: true },
          { path: "scheduled-operation/form", element: <ScheduledOperationForm /> },
          { path: "scheduled-operation/grid", element: <ScheduledOperationGrid /> },
          { path: "scheduled-operation/:id/form", element: <ScheduledOperationForm /> },
          { path: "scheduled-operation/:id/:copy/form", element: <ScheduledOperationForm /> },
        ],
      },
      {
        path: "location",
        children: [{ path: "form", element: <AssetForm type={endpointLocation} /> }],
      },
      {
        path: "equipment",
        children: [{ path: "form", element: <AssetForm type={endpointEquipment} /> }],
      },
      {
        path: "component",
        children: [
          { element: <ComponentGrid />, index: true },
          { path: "form", element: <ComponentForm /> },
          { path: "grid", element: <ComponentGrid /> },
          { path: ":id/form", element: <ComponentForm /> },
          { path: ":id/:copy/form", element: <ComponentForm /> },
          { path: ":id/undelete", element: <ComponentForm undelete /> },
          { path: "import", element: <ComponentImport /> },
        ],
      },
      {
        path: "asset",
        children: [
          { element: <AssetGrid />, index: true },
          { path: "grid", element: <AssetGrid /> },
          { path: "form", element: <AssetForm /> },
          { path: ":id/form", element: <AssetForm /> },
          { path: "location/:id/form", element: <AssetForm type={endpointLocation} /> },
          {
            path: "location/:id/with-parent",
            element: (
              <AssetForm
                type={endpointAsset}
                withParent
              />
            ),
          },
          { path: "equipment/:id/form", element: <AssetForm type={endpointEquipment} /> },
          {
            path: "equipment/:id/with-parent",
            element: (
              <AssetForm
                type={endpointAsset}
                withParent
              />
            ),
          },
          {
            path: "location/:id/copy",
            element: (
              <AssetForm
                type={endpointLocation}
                isCopy
              />
            ),
          },
          {
            path: "equipment/:id/copy",
            element: (
              <AssetForm
                type={endpointEquipment}
                isCopy
              />
            ),
          },
          {
            path: "equipment/:id/undelete",
            element: (
              <AssetForm
                type={endpointEquipment}
                undelete
              />
            ),
          },
          {
            path: "location/:id/undelete",
            element: (
              <AssetForm
                type={endpointLocation}
                undelete
              />
            ),
          },
          { path: "location/:id/detail", element: <AssetForm type={endpointLocation} /> },
          { path: "equipment/:id/detail", element: <AssetForm type={endpointEquipment} /> },
          {
            path: "location/:id/attachments",
            element: <AssetAttachments type={endpointLocation} />,
          },
          {
            path: "equipment/:id/attachments",
            element: <AssetAttachments type={endpointEquipment} />,
          },
          { path: "import", element: <AssetImport /> },
        ],
      },
      {
        path: "meter",
        children: [
          { element: <MeterGrid />, index: true },
          { path: "form", element: <MeterForm /> },
          { path: "grid", element: <MeterGrid /> },
          { path: ":id/form", element: <MeterForm /> },
          { path: ":id/undelete", element: <MeterForm undelete /> },
          { path: "readings/import", element: <ImportReading /> },
          { path: "reading", element: <ReadingGrid /> },
          { path: "reading/grid", element: <ReadingGrid /> },
          { path: "reading/readings/import", element: <ImportReading /> },
        ],
      },

      {
        path: "structure",
        children: [
          { element: <StructureGrid />, index: true },
          { path: "form", element: <StructureForm /> },
          { path: "grid", element: <StructureGrid /> },
          { path: ":id/form", element: <StructureForm /> },
          { path: ":id/:copy/form", element: <StructureForm /> },
          { path: ":id/undelete", element: <StructureForm undelete /> },
          { path: "import", element: <StructureImport /> },
        ],
      },
      {
        path: "user",
        children: [
          { element: <UserGrid />, index: true },
          { path: "profile", element: <UserProfile /> },
          { path: "grid", element: <UserGrid /> },
          { path: "form", element: <UserForm /> },
          { path: "import", element: <UserImport /> },
          { path: ":id/form", element: <UserForm /> },
          { path: ":id/:copy/form", element: <UserForm /> },
          { path: ":id/attachments", element: <UserAttachments /> },
          { path: ":id/detail", element: <UserForm /> },
          { path: "user-role", element: <UserRoleGrid />, index: true },
          { path: "user-role/grid", element: <UserRoleGrid /> },
          { path: "user-role/form", element: <UserRoleForm /> },
          { path: "user-role/:id/form", element: <UserRoleForm /> },
          { path: "user-role/:id/:copy/form", element: <UserRoleForm /> },
          { path: ":id/undelete", element: <UserForm undelete /> },
          { path: "user-role/:id/undelete", element: <UserRoleForm undelete /> },
          { path: "workTeam", element: <WorkTeamForm /> },
          { path: "workTeam/form", element: <WorkTeamForm /> },
        ],
      },
      {
        path: "product",
        children: [
          { element: <ProductListPage />, index: true },
          { path: "list", element: <ProductListPage /> },
          { path: ":id", element: <ProductDetailsPage /> },
          { path: "new", element: <ProductCreatePage /> },
          { path: ":id/edit", element: <ProductEditPage /> },
        ],
      },
      {
        path: "order",
        children: [
          { element: <OrderListPage />, index: true },
          { path: "list", element: <OrderListPage /> },
          { path: ":id", element: <OrderDetailsPage /> },
        ],
      },
      {
        path: "invoice",
        children: [
          { element: <InvoiceListPage />, index: true },
          { path: "list", element: <InvoiceListPage /> },
          { path: ":id", element: <InvoiceDetailsPage /> },
          { path: ":id/edit", element: <InvoiceEditPage /> },
          { path: "new", element: <InvoiceCreatePage /> },
        ],
      },
      {
        path: "post",
        children: [
          { element: <BlogPostsPage />, index: true },
          { path: "list", element: <BlogPostsPage /> },
          { path: ":title", element: <BlogPostPage /> },
          { path: ":title/edit", element: <BlogEditPostPage /> },
          { path: "new", element: <BlogNewPostPage /> },
        ],
      },
      {
        path: "job",
        children: [
          { element: <JobListPage />, index: true },
          { path: "list", element: <JobListPage /> },
          { path: ":id", element: <JobDetailsPage /> },
          { path: "new", element: <JobCreatePage /> },
          { path: ":id/edit", element: <JobEditPage /> },
        ],
      },
      {
        path: "tour",
        children: [
          { element: <TourListPage />, index: true },
          { path: "list", element: <TourListPage /> },
          { path: ":id", element: <TourDetailsPage /> },
          { path: "new", element: <TourCreatePage /> },
          { path: ":id/edit", element: <TourEditPage /> },
        ],
      },
      {
        path: "system",
        children: [
          { element: <GlobalConfigurationPage />, index: true },
          { path: "labels", element: <LabelEditPage /> },
          { path: "globalConfiguration", element: <GlobalConfigurationPage /> },
          { path: "mcpConnections", element: <McpConnectionsPage /> },
        ],
      },
      { path: "file-manager", element: <FileManagerPage /> },
      { path: "mail", element: <MailPage /> },
      { path: "chat", element: <ChatPage /> },
      { path: "calendar", element: <CalendarPage /> },
      { path: "kanban", element: <KanbanPage /> },
      { path: "permission", element: <PermissionDeniedPage /> },
      { path: "blank", element: <BlankPage /> },
      { path: "open/:entityType", element: <EntityRedirectPage /> },
      { path: "open/:entityType/:id", element: <EntityRedirectPage /> },
      {
        path: "inventory",
        children: [
          { element: <VoucherGrid />, index: true },
          { path: "grid", element: <VoucherGrid /> },
          { path: "form", element: <VoucherForm /> },
          { path: ":id/form", element: <VoucherForm /> },
          { path: ":id/attachments", element: <VoucherAttachments /> },
          { path: ":id/document", element: <VoucherDocument /> },
          { path: "voucher-type", element: <VoucherTypeGrid />, index: true },
          { path: "voucher-type/grid", element: <VoucherTypeGrid /> },
          { path: "voucher-type/form", element: <VoucherTypeForm /> },
          { path: "voucher-type/:id/form", element: <VoucherTypeForm /> },
        ],
      },
    ],
  },
];
